import { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CloseFollowupDialog from "@/components/CloseFollowupDialog";
import { fmtDate, fmtDateTime } from "@/lib/entries";
import { CalendarClock, PhoneCall, MessageCircle, XCircle, Clock, User, Plus } from "lucide-react";

export default function PatientHistoryDialog({ open, onOpenChange, patientName }) {
  const [events, setEvents] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entryFilter, setEntryFilter] = useState("all");
  // quick-add call form
  const [showForm, setShowForm] = useState(false);
  const [kind, setKind] = useState("call");
  const [channel, setChannel] = useState("whatsapp");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // close dialog
  const [closeEntry, setCloseEntry] = useState(null);
  const latestRef = useRef(null);

  const load = async () => {
    if (!patientName) return;
    try {
      const { data } = await api.get("/patients/history", { params: { name: patientName } });
      setEvents((data.events || []).slice().reverse());
      setEntries(data.entries || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (!open || !patientName) return;
    setLoading(true); setEntryFilter("all"); setShowForm(false); setNote("");
    (async () => { await load(); setLoading(false); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientName]);

  // Auto-focus the latest event as soon as events populate or the filter changes.
  useEffect(() => {
    if (loading || !filtered.length) return;
    const t = setTimeout(() => {
      latestRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, entryFilter, events.length]);

  const openEntry = useMemo(() => entries.find((e) => !e.is_complete) || null, [entries]);
  const filtered = useMemo(() => {
    if (entryFilter === "all") return events;
    return events.filter((e) => e.entry_id === entryFilter);
  }, [events, entryFilter]);

  const submitComm = async (e) => {
    e.preventDefault();
    if (!openEntry) { toast.error("No open follow-up to log against"); return; }
    if (!note.trim()) { toast.error("Please add a note"); return; }
    setSaving(true);
    try {
      await api.post(`/entries/${openEntry.id}/communications`, {
        kind, channel: kind === "message" ? channel : null, note,
      });
      setNote(""); setShowForm(false);
      toast.success("Communication logged");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
                       data-testid="patient-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <User size={18} className="text-slate-700" /> {patientName}
            </DialogTitle>
            <DialogDescription>
              Complete chronological history — every follow-up scheduled, call, message and closure for this patient. Newest first.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">Loading...</div>
          ) : events.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="patient-history-empty">
              No follow-up or communication history yet for this patient.
            </div>
          ) : (
            <>
              {/* Filter + quick actions */}
              <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-slate-100">
                <Select value={entryFilter} onValueChange={setEntryFilter}>
                  <SelectTrigger className="w-full sm:w-64" data-testid="patient-history-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All follow-ups ({entries.length})</SelectItem>
                    {entries.map((en, i) => (
                      <SelectItem key={en.id} value={en.id} data-testid={`patient-history-filter-${i}`}>
                        #{i + 1} · {fmtDate(en.scheduled_date || en.entry_date)}{en.is_complete ? " · closed" : " · open"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {openEntry && (
                  <div className="flex items-center gap-2 ml-auto">
                    <Button size="sm" variant="outline"
                            onClick={() => setShowForm((v) => !v)}
                            data-testid="patient-history-add-comm">
                      <Plus size={14} className="mr-1" /> Log call/message
                    </Button>
                    <Button size="sm" onClick={() => setCloseEntry(openEntry)}
                            className="bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="patient-history-close-followup">
                      <XCircle size={14} className="mr-1" /> Close follow-up
                    </Button>
                  </div>
                )}
              </div>

              {/* Inline comm form */}
              {showForm && openEntry && (
                <form onSubmit={submitComm}
                      className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3"
                      data-testid="patient-history-comm-form">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Type</Label>
                      <Select value={kind} onValueChange={setKind}>
                        <SelectTrigger data-testid="ph-kind-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="call">Call</SelectItem>
                          <SelectItem value="message">Message</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {kind === "message" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Channel</Label>
                        <Select value={channel} onValueChange={setChannel}>
                          <SelectTrigger data-testid="ph-channel-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="whatsapp">WhatsApp</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Note</Label>
                    <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                              placeholder="What did they say?" data-testid="ph-note-input" />
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" size="sm" disabled={saving}
                            className="bg-sky-500 hover:bg-sky-600 text-white"
                            data-testid="ph-submit-button">
                      {saving ? "Saving..." : "Log"}
                    </Button>
                  </div>
                </form>
              )}

              <div className="text-xs text-slate-500 mt-3">
                {entries.length} follow-up{entries.length === 1 ? "" : "s"} · {events.filter((e) => e.type === "communication").length} communications
                {entryFilter !== "all" && <> · showing {filtered.length} events</>}
              </div>
              <ol className="mt-3 relative border-l border-slate-200 ml-3">
                {filtered.map((e, i) => {
                  let Icon = Clock, tone = "text-slate-500", title = "", body = e.note || "";
                  if (e.type === "scheduled") {
                    Icon = CalendarClock; tone = "text-sky-600";
                    title = "Follow-up scheduled";
                  } else if (e.type === "communication") {
                    Icon = e.kind === "call" ? PhoneCall : MessageCircle;
                    tone = e.kind === "call" ? "text-sky-600" : "text-emerald-600";
                    title = e.kind === "call" ? "Call logged" : `Message · ${e.channel || "—"}`;
                  } else if (e.type === "closed") {
                    Icon = XCircle; tone = "text-slate-700";
                    title = `Follow-up closed · ${e.reason || ""}`;
                  }
                  return (
                    <li key={i}
                        ref={i === 0 ? latestRef : null}
                        className={`ml-6 pb-5 ${i === 0 ? "-mx-1 px-1 rounded" : ""}`}
                        data-testid={`patient-history-event-${i}`}>
                      <span className="absolute -left-[7px] mt-1.5 h-3.5 w-3.5 rounded-full bg-white ring-4 ring-slate-100 flex items-center justify-center">
                        <Icon size={9} className={tone} />
                      </span>
                      <div className="text-sm text-slate-500 inline-flex items-center gap-1">
                        <Clock size={12} />
                        {e.type === "scheduled" ? fmtDate(e.at) : fmtDateTime(e.at)}
                        {i === 0 && (
                          <Badge className="ml-2 bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                                 data-testid="patient-history-latest-badge">Latest</Badge>
                        )}
                        {e.legacy_outcome && (
                          <Badge className="ml-2 bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100">{e.legacy_outcome}</Badge>
                        )}
                      </div>
                      <div className="text-slate-900 font-medium mt-0.5">{title}</div>
                      {body && <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{body}</div>}
                      {e.by && <div className="text-xs text-slate-400 mt-0.5">by {e.by}</div>}
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CloseFollowupDialog
        open={!!closeEntry}
        onOpenChange={(v) => !v && setCloseEntry(null)}
        entry={closeEntry}
        onClosed={load}
      />
    </>
  );
}
